<template>
  <div class="image-uploader" :class="`shape-${shape}`">
    <div
      class="image-preview-wrap"
      :class="{ 'natural-ratio': usingNaturalRatio }"
      :style="previewWrapStyle"
    >
      <img
        v-if="modelValue"
        :src="modelValue"
        :alt="alt"
        class="image-preview-img"
        @load="onPreviewLoad"
      />
      <div v-else class="image-placeholder">
        <i :class="placeholderIcon"></i>
        <span>{{ placeholder }}</span>
        <small v-if="hint">{{ hint }}</small>
      </div>
      <div v-if="uploading" class="image-overlay">
        <div class="upload-spinner"></div>
        <span>{{ uploadStatus === 'cropping' ? '處理中...' : uploadStatus === 'resizing' ? '縮圖處理中...' : '上傳中...' }}</span>
      </div>
    </div>

    <div class="image-actions">
      <label class="btn btn-primary btn-upload-label" :class="{ disabled: uploading }">
        <i class="ri-upload-cloud-line"></i>
        {{ modelValue ? replaceLabel : uploadLabel }}
        <input
          type="file"
          :accept="accept"
          class="hidden-file-input"
          :disabled="uploading"
          @change="handleFileChange"
        />
      </label>
      <button
        v-if="modelValue"
        type="button"
        class="btn btn-danger-outline"
        :disabled="uploading"
        @click="removeImage"
      >
        <i class="ri-delete-bin-line"></i>
        移除
      </button>
    </div>

    <div class="upload-hint-row">
      <p v-if="sizeHint" class="upload-hint">{{ sizeHint }}</p>
      <span
        v-if="fileSizeText"
        class="file-size-badge"
        :class="fileSizeStatus"
      >
        <i class="ri-file-image-line"></i>
        {{ fileSizeText }}
      </span>
    </div>

    <!-- 裁切編輯器 Modal -->
    <div v-if="showCropModal" class="crop-backdrop" @mousedown.self="cancelCrop">
      <div class="crop-modal">
        <div class="crop-modal-header">
          <div>
            <h3 class="crop-title">調整裁切</h3>
            <p class="crop-subtitle">拖曳移動位置，滾輪或下方滑桿可放大縮小</p>
          </div>
          <button class="crop-close-btn" @click="cancelCrop"><i class="ri-close-line"></i></button>
        </div>

        <!-- 縱長裁切時中間區超過上限可捲動；不用 flex:1 以免父層高度 auto 時中間區高度塌成 0 -->
        <div class="crop-modal-body">
          <div
            ref="viewport"
            class="crop-viewport"
            :style="{ aspectRatio: `${cropWidth} / ${cropHeight}` }"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
            @touchstart.prevent="startDragTouch"
            @touchmove.prevent="onDragTouch"
            @touchend="endDrag"
            @wheel.prevent="onWheelZoom"
          >
            <img
              ref="cropImg"
              :src="cropSrc"
              class="crop-image"
              :style="{
                width: imgDisplayW + 'px',
                height: imgDisplayH + 'px',
                transform: `translate(${cropOffsetX}px, ${cropOffsetY}px)`,
              }"
              draggable="false"
              @load="onImgLoad"
            />
            <!-- 三等份格線 -->
            <div class="crop-grid">
              <div class="crop-grid-line v" style="left:33.33%"></div>
              <div class="crop-grid-line v" style="left:66.66%"></div>
              <div class="crop-grid-line h" style="top:33.33%"></div>
              <div class="crop-grid-line h" style="top:66.66%"></div>
            </div>
            <!-- 尺寸提示 -->
            <div class="crop-size-badge">{{ cropWidth }} × {{ cropHeight }} px</div>
          </div>
        </div>

        <div class="crop-modal-footer">
          <div class="crop-zoom">
            <button
              type="button"
              class="crop-zoom-btn"
              :disabled="zoom <= 1"
              aria-label="縮小"
              @click="nudgeZoom(-0.1)"
            >
              <i class="ri-subtract-line"></i>
            </button>
            <input
              type="range"
              class="crop-zoom-slider"
              min="1"
              :max="maxZoom"
              step="0.05"
              :value="zoom"
              @input="setZoom(Number($event.target.value))"
            />
            <button
              type="button"
              class="crop-zoom-btn"
              :disabled="zoom >= maxZoom"
              aria-label="放大"
              @click="nudgeZoom(0.1)"
            >
              <i class="ri-add-line"></i>
            </button>
            <span class="crop-zoom-label">{{ Math.round(zoom * 100) }}%</span>
          </div>
          <div class="crop-footer-actions">
            <button class="btn btn-secondary" @click="cancelCrop">取消</button>
            <button class="btn btn-primary" @click="confirmCrop">
              <i class="ri-scissors-cut-line"></i> 確認裁切並上傳
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { showError } from '@/utils/swal.js';
import { resampleImageFile } from '@/utils/imageResample.js';

/** MIME → 顯示名稱（錯誤訊息用） */
const MIME_LABELS = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/gif': 'GIF'
};

export default {
  name: 'ImageUploader',
  props: {
    modelValue:     { type: String,   default: null },
    uploadFn:       { type: Function, required: true },
    shape:          { type: String,   default: 'square', validator: v => ['square','card','free'].includes(v) },
    aspectRatio:    { type: String,   default: '' },
    /** 為 true 時：預覽框改用圖片原始比例顯示（不裁切），未指定 aspectRatio 時才生效 */
    previewNaturalRatio: { type: Boolean, default: false },
    cropWidth:      { type: Number,   default: 0 },
    cropHeight:     { type: Number,   default: 0 },
    /** 為 true 時：僅在圖片寬高比與 crop 尺寸比例不符時開啟裁切；比例相符則直接上傳原檔 */
    cropOnlyIfAspectMismatch: { type: Boolean, default: false },
    /** 裁切確認後輸出 JPEG 的大小上限（MB）；0 表示與 maxSizeMb 相同（原圖驗證仍用 maxSizeMb） */
    cropOutputMaxMb: { type: Number, default: 0 },
    accept:         { type: String,   default: 'image/jpeg,image/png,image/webp' },
    maxSizeMb:      { type: Number,   default: 5 },
    /** >0 時：上傳前限制最長邊與檔案大小；預設轉 JPEG，preserveTransparency 可保留透明背景 */
    resizeMaxEdge:  { type: Number,   default: 0 },
    /** 縮圖時保留 PNG／WebP 的透明背景；未縮圖的檔案直接保留原格式 */
    preserveTransparency: { type: Boolean, default: false },
    /** 為 true 時：uploadFn 成功後不 emit modelValue（由 uploadFn 內自行更新父層多欄位） */
    skipEmitAfterSuccess: { type: Boolean, default: false },
    placeholder:    { type: String,   default: '上傳圖片' },
    placeholderIcon:{ type: String,   default: 'ri-image-add-line' },
    hint:           { type: String,   default: '' },
    sizeHint:       { type: String,   default: '' },
    alt:            { type: String,   default: '圖片預覽' },
    uploadLabel:    { type: String,   default: '上傳圖片' },
    replaceLabel:   { type: String,   default: '更換圖片' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    /* 允許的檔案類型以 accept 為單一來源，避免畫面可選但驗證擋掉（或反之） */
    const allowedTypes = computed(
      () => props.accept.split(',').map(s => s.trim()).filter(Boolean)
    );
    const allowedTypeLabels = computed(
      () => allowedTypes.value.map(t => MIME_LABELS[t] || t).join('、')
    );

    const uploading    = ref(false);
    const uploadStatus = ref('');       // '' | 'cropping' | 'uploading'
    const showCropModal = ref(false);
    const currentFileSize = ref(0);     // bytes，上傳成功後記錄

    const fileSizeText = computed(() => {
      if (!currentFileSize.value) return '';
      const kb = currentFileSize.value / 1024;
      if (kb < 1024) return `${Math.round(kb)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    });

    const fileSizeStatus = computed(() => {
      if (!currentFileSize.value || !props.maxSizeMb) return '';
      const ratio = currentFileSize.value / (props.maxSizeMb * 1024 * 1024);
      if (ratio >= 0.9) return 'size-warn';
      if (ratio >= 0.7) return 'size-ok';
      return 'size-good';
    });
    const cropSrc       = ref('');
    const cropImg       = ref(null);
    const viewport      = ref(null);

    /* ── 預覽框比例（previewNaturalRatio 用）── */
    const naturalRatio = ref('');
    const usingNaturalRatio = computed(
      () => props.previewNaturalRatio && !props.aspectRatio && !!naturalRatio.value
    );
    const previewWrapStyle = computed(() => {
      if (props.aspectRatio) return { aspectRatio: props.aspectRatio, height: 'auto' };
      if (usingNaturalRatio.value) return { aspectRatio: naturalRatio.value, height: 'auto' };
      return {};
    });
    const onPreviewLoad = (e) => {
      if (!props.previewNaturalRatio) return;
      const { naturalWidth: w, naturalHeight: h } = e.target;
      naturalRatio.value = w && h ? `${w} / ${h}` : '';
    };
    watch(() => props.modelValue, () => { naturalRatio.value = ''; });

    // 圖片在 viewport 中的顯示尺寸與位移
    const imgDisplayW = ref(0);
    const imgDisplayH = ref(0);
    const cropOffsetX = ref(0);
    const cropOffsetY = ref(0);
    /** 相對「剛好蓋滿裁切框」的倍率；1 = 最小（cover），再小會露出空白 */
    const zoom = ref(1);
    const maxZoom = 4;

    // 自然尺寸
    let naturalW = 0, naturalH = 0;
    // cover 倍率（zoom=1 時）
    let coverScale = 1;
    // 拖曳狀態
    let dragging = false, lastX = 0, lastY = 0;
    // 雙指縮放
    let pinching = false;
    let pinchStartDist = 0;
    let pinchStartZoom = 1;
    // 待上傳的原始 File
    let pendingFile = null;
    // viewport 目前寬高
    let vpW = 0, vpH = 0;

    const applyDisplaySize = () => {
      imgDisplayW.value = Math.round(naturalW * coverScale * zoom.value);
      imgDisplayH.value = Math.round(naturalH * coverScale * zoom.value);
    };

    /* ── 拖曳限制（圖片不能露出 viewport 邊緣）── */
    const clampOffset = (ox, oy) => {
      const minX = vpW - imgDisplayW.value;
      const minY = vpH - imgDisplayH.value;
      return {
        x: Math.min(0, Math.max(minX, ox)),
        y: Math.min(0, Math.max(minY, oy)),
      };
    };

    const applyOffset = (ox, oy) => {
      const clamped = clampOffset(ox, oy);
      cropOffsetX.value = clamped.x;
      cropOffsetY.value = clamped.y;
    };

    /**
     * 以 viewport 內某點為錨點改倍率，該點對應的圖上位置保持不動
     */
    const applyZoom = (nextZoom, originX = vpW / 2, originY = vpH / 2) => {
      const oldW = imgDisplayW.value;
      const oldH = imgDisplayH.value;
      if (!oldW || !oldH) return;
      const z = Math.min(maxZoom, Math.max(1, nextZoom));
      zoom.value = z;
      applyDisplaySize();
      const ratioX = (originX - cropOffsetX.value) / oldW;
      const ratioY = (originY - cropOffsetY.value) / oldH;
      applyOffset(
        originX - ratioX * imgDisplayW.value,
        originY - ratioY * imgDisplayH.value
      );
    };

    const setZoom = (value) => applyZoom(value);
    const nudgeZoom = (delta) => applyZoom(zoom.value + delta);

    const onWheelZoom = (e) => {
      const vp = viewport.value;
      if (!vp) return;
      const rect = vp.getBoundingClientRect();
      const step = e.deltaY > 0 ? -0.12 : 0.12;
      applyZoom(zoom.value + step, e.clientX - rect.left, e.clientY - rect.top);
    };

    /* ── 初始化 viewport 中的圖片位置 ── */
    const onImgLoad = () => {
      const vp = viewport.value;
      if (!vp || !cropImg.value) return;
      vpW = vp.clientWidth;
      vpH = vp.clientHeight;
      naturalW = cropImg.value.naturalWidth;
      naturalH = cropImg.value.naturalHeight;

      // 計算 cover 尺寸（至少填滿 viewport）；之後只能往上放大
      const scaleX = vpW / naturalW;
      const scaleY = vpH / naturalH;
      coverScale = Math.max(scaleX, scaleY);
      zoom.value = 1;
      applyDisplaySize();

      // 置中
      applyOffset(
        Math.round((vpW - imgDisplayW.value) / 2),
        Math.round((vpH - imgDisplayH.value) / 2)
      );
    };

    /* ── Mouse 拖曳 ── */
    const startDrag = (e) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onDrag = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      applyOffset(cropOffsetX.value + dx, cropOffsetY.value + dy);
    };
    const endDrag = () => {
      dragging = false;
      pinching = false;
    };

    const touchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const touchMidpoint = (touches) => {
      const vp = viewport.value;
      if (!vp) return { x: vpW / 2, y: vpH / 2 };
      const rect = vp.getBoundingClientRect();
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
        y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
      };
    };

    /* ── Touch 拖曳／雙指縮放 ── */
    const startDragTouch = (e) => {
      if (e.touches.length === 2) {
        pinching = true;
        dragging = false;
        pinchStartDist = touchDistance(e.touches);
        pinchStartZoom = zoom.value;
        return;
      }
      if (e.touches.length !== 1) return;
      pinching = false;
      dragging = true;
      lastX = e.touches[0].clientX;
      lastY = e.touches[0].clientY;
    };
    const onDragTouch = (e) => {
      if (e.touches.length === 2 && pinchStartDist > 0) {
        pinching = true;
        dragging = false;
        const mid = touchMidpoint(e.touches);
        applyZoom(pinchStartZoom * (touchDistance(e.touches) / pinchStartDist), mid.x, mid.y);
        return;
      }
      if (!dragging || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - lastX;
      const dy = e.touches[0].clientY - lastY;
      lastX = e.touches[0].clientX;
      lastY = e.touches[0].clientY;
      applyOffset(cropOffsetX.value + dx, cropOffsetY.value + dy);
    };

    /* ── 取消裁切 ── */
    const cancelCrop = () => {
      showCropModal.value = false;
      if (cropSrc.value) URL.revokeObjectURL(cropSrc.value);
      cropSrc.value   = '';
      pendingFile     = null;
      zoom.value      = 1;
    };

    /* ── 確認裁切 → 繪製 Canvas → 上傳 ── */
    const confirmCrop = async () => {
      if (!cropImg.value || !props.cropWidth || !props.cropHeight) return;

      showCropModal.value = false;

      // 計算圖片在 viewport 中的可見區域（自然像素座標）
      const displayScale = imgDisplayW.value / naturalW;   // viewport px / natural px
      const srcX = (-cropOffsetX.value) / displayScale;
      const srcY = (-cropOffsetY.value) / displayScale;
      const srcW = vpW / displayScale;
      const srcH = vpH / displayScale;

      uploading.value    = true;
      uploadStatus.value = 'cropping';

      let fileToUpload;
      try {
        fileToUpload = await new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          canvas.width  = props.cropWidth;
          canvas.height = props.cropHeight;
          const ctx = canvas.getContext('2d');

          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, props.cropWidth, props.cropHeight);
            const outMb = props.cropOutputMaxMb > 0 ? props.cropOutputMaxMb : props.maxSizeMb;
            const maxBytes = outMb * 1024 * 1024;
            const tryQ = (q) => {
              canvas.toBlob(blob => {
                if (!blob) { reject(new Error('裁切失敗')); return; }
                if (blob.size <= maxBytes) {
                  resolve(new File([blob], pendingFile.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
                } else if (q <= 0.5) {
                  reject(new Error(`圖片裁切後仍超過 ${outMb}MB（目前 ${(blob.size / 1024 / 1024).toFixed(2)}MB），請使用較小的原始圖片`));
                } else {
                  tryQ(Math.round((q - 0.1) * 10) / 10);
                }
              }, 'image/jpeg', q);
            };
            tryQ(0.92);
          };
          img.onerror = () => reject(new Error('圖片載入失敗'));
          img.src = cropSrc.value;   // 使用 Object URL
        });
      } catch (err) {
        uploading.value = false;
        uploadStatus.value = '';
        URL.revokeObjectURL(cropSrc.value);
        cropSrc.value = '';
        await showError('裁切失敗', err.message);
        return;
      }

      uploadStatus.value = 'uploading';
      try {
        const res = await props.uploadFn(fileToUpload);
        if (res?.success && res?.data?.fileUrl) {
          currentFileSize.value = fileToUpload.size;
          emit('update:modelValue', res.data.fileUrl);
        } else {
          await showError('上傳失敗', res?.message || '無法取得圖片 URL');
        }
      } catch (err) {
        await showError('上傳失敗', err.message || '上傳發生錯誤');
      } finally {
        uploading.value    = false;
        uploadStatus.value = '';
        URL.revokeObjectURL(cropSrc.value);
        cropSrc.value = '';
        pendingFile   = null;
      }
    };

    async function resizeImageFile(file, maxEdge, maxBytes) {
      if (!maxEdge || maxEdge < 1) return file;
      return resampleImageFile(file, { maxEdge, maxBytes, preserveTransparency: props.preserveTransparency });
    }

    function loadNaturalDimensions(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('無法讀取圖片'));
        };
        img.src = url;
      });
    }

    function isAspectRatioClose(nw, nh, targetW, targetH) {
      if (nw <= 0 || nh <= 0 || targetW <= 0 || targetH <= 0) return false;
      const actual = nw / nh;
      const target = targetW / targetH;
      return Math.abs(actual - target) / target < 0.008;
    }

    async function uploadOriginalFile(file) {
      let f = file;
      uploading.value = true;
      uploadStatus.value = props.resizeMaxEdge > 0 ? 'resizing' : 'uploading';
      try {
        if (props.resizeMaxEdge > 0) {
          f = await resizeImageFile(file, props.resizeMaxEdge, props.maxSizeMb * 1024 * 1024);
          uploadStatus.value = 'uploading';
        } else if (f.size > props.maxSizeMb * 1024 * 1024) {
          await showError('檔案過大', `圖片大小不能超過 ${props.maxSizeMb}MB`);
          return;
        }

        if (f.size > props.maxSizeMb * 1024 * 1024) {
          await showError('檔案過大', `圖片大小不能超過 ${props.maxSizeMb}MB`);
          return;
        }

        const res = await props.uploadFn(f);
        if (res?.success && res?.data?.fileUrl) {
          currentFileSize.value = f.size;
          if (!props.skipEmitAfterSuccess) {
            emit('update:modelValue', res.data.fileUrl);
          }
        } else {
          await showError('上傳失敗', res?.message || '無法取得圖片 URL');
        }
      } catch (err) {
        const msg = err?.message || '上傳發生錯誤';
        const title = /縮圖|壓縮|讀取|尺寸|無效/.test(msg) ? '圖片處理失敗' : '上傳失敗';
        await showError(title, msg);
      } finally {
        uploading.value = false;
        uploadStatus.value = '';
      }
    }

    /* ── 選擇檔案 ── */
    const handleFileChange = async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!allowedTypes.value.includes(file.type)) {
        await showError('檔案類型錯誤', `僅支援 ${allowedTypeLabels.value} 格式`);
        return;
      }

      // 若有設定裁切尺寸
      if (props.cropWidth > 0 && props.cropHeight > 0) {
        if (props.cropOnlyIfAspectMismatch) {
          try {
            const { width: nw, height: nh } = await loadNaturalDimensions(file);
            if (isAspectRatioClose(nw, nh, props.cropWidth, props.cropHeight)) {
              await uploadOriginalFile(file);
              return;
            }
          } catch {
            /* 讀取失敗時改走裁切 */
          }
        }
        pendingFile = file;
        cropSrc.value = URL.createObjectURL(file);
        showCropModal.value = true;
        return;
      }

      await uploadOriginalFile(file);
    };

    const removeImage = () => {
      currentFileSize.value = 0;
      emit('update:modelValue', null);
    };

    return {
      uploading, uploadStatus,
      allowedTypeLabels,
      usingNaturalRatio, previewWrapStyle, onPreviewLoad,
      showCropModal, cropSrc, cropImg, viewport,
      imgDisplayW, imgDisplayH, cropOffsetX, cropOffsetY,
      zoom, maxZoom, setZoom, nudgeZoom, onWheelZoom,
      currentFileSize, fileSizeText, fileSizeStatus,
      onImgLoad,
      startDrag, onDrag, endDrag,
      startDragTouch, onDragTouch,
      cancelCrop, confirmCrop,
      handleFileChange,
      removeImage,
    };
  }
};
</script>

<style scoped>
.image-uploader {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── 預覽區域 ── */
.image-preview-wrap {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.shape-square .image-preview-wrap { width: 160px; height: 160px; }
.shape-card   .image-preview-wrap { width: min(320px, 100%); aspect-ratio: 320 / 202; height: auto; }
.shape-free   .image-preview-wrap { width: 100%; max-width: 400px; aspect-ratio: 3 / 2; }

.image-preview-img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 6px;
}

/*
 * 原圖比例模式：預覽框高度由 aspect-ratio 決定，極端長圖用 max-height 收住；
 * 被 max-height 截斷時改用 contain，確保看到的仍是完整原圖比例。
 */
.shape-square .natural-ratio,
.shape-card   .natural-ratio,
.shape-free   .natural-ratio { height: auto; max-height: 460px; }
.natural-ratio .image-preview-img { object-fit: contain; }

.image-placeholder {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 6px; color: #9ca3af; padding: 12px; text-align: center;
}
.image-placeholder i    { font-size: 28px; color: #d1d5db; }
.image-placeholder span { font-size: 13px; font-weight: 500; }
.image-placeholder small{ font-size: 11px; color: #b0b7c3; }

.image-overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,.5);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px; color: #fff; font-size: 13px; border-radius: 6px;
}
.upload-spinner {
  width: 24px; height: 24px;
  border: 3px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── 操作按鈕 ── */
.image-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.btn-upload-label {
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px; margin: 0;
}
.btn-upload-label.disabled { opacity: .6; pointer-events: none; cursor: not-allowed; }
.hidden-file-input { display: none; }
.upload-hint-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.upload-hint { font-size: 12px; color: #6b7280; margin: 0; }

.file-size-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}
.file-size-badge i { font-size: 12px; }
.file-size-badge.size-good  { background: #d1fae5; color: #065f46; }
.file-size-badge.size-ok    { background: #fef3c7; color: #92400e; }
.file-size-badge.size-warn  { background: #fee2e2; color: #991b1b; }

/* ══════════════════════════════════════════
   裁切編輯器
══════════════════════════════════════════ */
.crop-backdrop {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.65);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}

.crop-modal {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0,0,0,.35);
  width: min(720px, 100%);
  max-height: 90vh;
  display: flex; flex-direction: column;
  overflow: hidden;
}

.crop-modal-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}
.crop-title { font-size: 16px; font-weight: 600; margin: 0 0 2px; }
.crop-subtitle { font-size: 12px; color: #6b7280; margin: 0; }
.crop-close-btn {
  background: none; border: none; cursor: pointer;
  font-size: 20px; color: #9ca3af; padding: 2px 6px; border-radius: 6px;
  transition: background .15s;
}
.crop-close-btn:hover { background: #f3f4f6; color: #374151; }

/*
 * 以 max-height 限制裁切區，避免縱長比例時整個 modal 超出視窗；
 * 內容較矮時高度隨裁切視窗變化（不強制 flex:1，否則 modal 高度為 auto 時中間會變 0 高）。
 */
.crop-modal-body {
  max-height: max(10rem, calc(90vh - 190px));
  max-height: max(10rem, calc(90dvh - 190px));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* 裁切視窗 */
.crop-viewport {
  position: relative;
  width: 100%;
  overflow: hidden;
  cursor: grab;
  background: #111;
  user-select: none;
}
.crop-viewport:active { cursor: grabbing; }

.crop-image {
  position: absolute;
  top: 0; left: 0;
  display: block;
  will-change: transform;
  pointer-events: none;
}

/* 格線 */
.crop-grid { position: absolute; inset: 0; pointer-events: none; }
.crop-grid-line {
  position: absolute;
  background: rgba(255,255,255,.3);
}
.crop-grid-line.v { width: 1px; top: 0; bottom: 0; }
.crop-grid-line.h { height: 1px; left: 0; right: 0; }

/* 尺寸標示 */
.crop-size-badge {
  position: absolute; bottom: 8px; right: 10px;
  background: rgba(0,0,0,.55); color: #fff;
  font-size: 11px; padding: 2px 8px; border-radius: 20px;
  pointer-events: none;
}

.crop-modal-footer {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
  background: #fff;
  flex-wrap: wrap;
}
.crop-zoom {
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 200px;
}
.crop-zoom-btn {
  width: 28px; height: 28px; padding: 0;
  border: 1px solid #d1d5db; border-radius: 6px;
  background: #fff; color: #374151; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}
.crop-zoom-btn:hover:not(:disabled) { border-color: #3b82f6; color: #3b82f6; }
.crop-zoom-btn:disabled { opacity: .35; cursor: not-allowed; }
.crop-zoom-slider {
  flex: 1; min-width: 80px; accent-color: #3b82f6; cursor: pointer;
}
.crop-zoom-label {
  font-size: 12px; color: #6b7280; min-width: 40px; text-align: right;
  font-variant-numeric: tabular-nums;
}
.crop-footer-actions { display: flex; gap: 10px; flex-shrink: 0; }
</style>
